import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  AlertActionLink,
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  Label,
  LabelGroup,
  MenuToggle,
  Pagination,
  SearchInput,
  Select,
  SelectList,
  SelectOption,
  Spinner,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
  Tooltip,
} from '@patternfly/react-core';
import {
  AngleLeftIcon,
  AngleRightIcon,
  CheckCircleIcon,
  DownloadIcon,
  ExclamationCircleIcon,
  InfoCircleIcon,
  SearchIcon,
  SyncAltIcon,
} from '@patternfly/react-icons';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { formatDateTimeWithSeconds, formatMonthYear } from '../../../utils/dateFormat';
import { formatCurrencyAmount, formatPrice } from '../../../utils/money';
import { downloadTextFile } from '../../../utils/fileDownload';
import { useDebouncedValue } from '../Verda/VerdaGpuParts';
import { buildBanners } from './reconciliationBanners';
import type { BannerContext } from './reconciliationBanners';
import { FLAG_INFO, MONEY_OWED_FLAGS } from './reconciliationLabels';
import type { FlagInfo } from './reconciliationLabels';
import { MIN_YEAR, isSelectableMonth, monthOptions, shiftMonth, yearOptions } from './reconciliationPeriod';
import type { ReconciliationWarning } from '../../../types/models/Reconciliation';

// Building blocks shared by every reconciliation tab and the payout detail page. The screens are in
// French; money is a decimal string formatted for display only (the backend computes every total).

// ── Money ────────────────────────────────────────────────────────────────────────────────────────────

const NUMERIC_STYLE: React.CSSProperties = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

/** A 2-decimal money string, fr-BE (`-3,82 €`). */
export const Money: React.FunctionComponent<{ value: string | null | undefined }> = ({ value }) => (
  <span style={NUMERIC_STYLE}>{value === null || value === undefined ? '—' : formatPrice(value)}</span>
);

/** One of Mollie's raw amounts (up to 5 decimals), every digit kept (`0,16335 €`). */
export const ExactMoney: React.FunctionComponent<{ value: string | null | undefined }> = ({ value }) => (
  <span style={NUMERIC_STYLE}>{value === null || value === undefined ? '—' : formatCurrencyAmount(value, 'EUR')}</span>
);

/** The right-aligned cell class for amount columns. */
export const AMOUNT_CELL = 'pf-v6-u-text-align-end';

// ── Status labels ────────────────────────────────────────────────────────────────────────────────────

/** A check result: green « Concordant » / red « Écart … ». */
export const CheckLabel: React.FunctionComponent<{ ok: boolean | null; difference?: string | null }> = ({
  ok,
  difference,
}) => {
  if (ok === null) {
    return (
      <Label color="grey" isCompact>
        Non émise
      </Label>
    );
  }
  return ok ? (
    <Label status="success" isCompact icon={<CheckCircleIcon />}>
      Concordant
    </Label>
  ) : (
    <Label status="danger" isCompact icon={<ExclamationCircleIcon />}>
      {difference ? `Écart ${formatPrice(difference)}` : 'Écart'}
    </Label>
  );
};

/** The row's flags, in French, each with what to look at as its tooltip. */
export const FlagLabels: React.FunctionComponent<{
  flags: string[];
  /** The flag vocabulary (journal / sales flags by default; the accounting entries have their own). */
  catalog?: Readonly<Record<string, FlagInfo>>;
  /** Flags rendered in red rather than orange. */
  severe?: ReadonlySet<string>;
}> = ({ flags, catalog = FLAG_INFO, severe = MONEY_OWED_FLAGS }) => {
  if (flags.length === 0) {
    return <>—</>;
  }
  return (
    <LabelGroup
      numLabels={2}
      isCompact
      aria-label="Signalements"
      expandedText="Moins"
      collapsedText="${remaining} de plus"
    >
      {flags.map((flag) => {
        const meaning = catalog[flag]?.meaning ?? null;
        const label = (
          <Label color={severe.has(flag) ? 'red' : 'orange'} isCompact>
            {catalog[flag]?.label ?? flag}
          </Label>
        );
        return meaning ? (
          <Tooltip key={flag} content={meaning}>
            {label}
          </Tooltip>
        ) : (
          <React.Fragment key={flag}>{label}</React.Fragment>
        );
      })}
    </LabelGroup>
  );
};

// ── Payout links ─────────────────────────────────────────────────────────────────────────────────────

export const settlementPath = (id: string) => `/admin/reconciliation/settlements/${encodeURIComponent(id)}`;

/**
 * A payout's bank reference, linked to its detail page. `open` (the not-yet-paid-out balance) is text:
 * it has no detail view. An inferred link (invoice compensations) carries a hint.
 */
export const SettlementRef: React.FunctionComponent<{
  id: string | null;
  reference: string | null;
  inferred?: boolean;
  /** Refunds / chargebacks: the payout that DEDUCTED the row, not the refunded payment's. */
  deducted?: boolean;
}> = ({ id, reference, inferred = false, deducted = false }) => {
  if (id === null) {
    return <span className="pf-v6-u-text-color-subtle">Pas encore versé</span>;
  }
  if (id === 'open') {
    return <span className="pf-v6-u-text-color-subtle">Solde non versé</span>;
  }
  const hint = inferred
    ? 'Versement déduit : la compensation est rattachée au versement qui porte sa ligne de compensation.'
    : deducted
      ? 'Le versement qui a déduit ce montant (pas celui du paiement remboursé).'
      : null;
  return (
    <span style={{ whiteSpace: 'nowrap' }}>
      <Link to={settlementPath(id)}>{reference ?? id}</Link>
      {hint && (
        <Tooltip content={hint}>
          <Button
            variant="plain"
            size="sm"
            aria-label={inferred ? 'Lien de versement déduit' : 'Versement qui a déduit ce montant'}
            icon={<InfoCircleIcon />}
            className="pf-v6-u-py-0 pf-v6-u-px-xs"
          />
        </Tooltip>
      )}
    </span>
  );
};

// ── Read states ──────────────────────────────────────────────────────────────────────────────────────

/** Live reads take time (Mollie, then Facturier, within a 25 s budget): say so under the spinner. */
export const LiveReadSpinner: React.FunctionComponent<{ what: string }> = ({ what }) => (
  <Flex
    direction={{ default: 'column' }}
    alignItems={{ default: 'alignItemsCenter' }}
    spaceItems={{ default: 'spaceItemsMd' }}
    className="pf-v6-u-py-2xl"
  >
    <FlexItem>
      <Spinner size="lg" aria-label={`Chargement : ${what}`} />
    </FlexItem>
    <FlexItem className="pf-v6-u-text-color-subtle">
      Lecture en direct de Mollie et de Facturier — cela peut prendre jusqu&apos;à 25 secondes.
    </FlexItem>
  </Flex>
);

/** A failed read with no data to show: the backend's message (in French) and a retry. */
export const ReadErrorState: React.FunctionComponent<{ title: string; message: string; onRetry: () => void }> = ({
  title,
  message,
  onRetry,
}) => (
  <EmptyState titleText={title} icon={ExclamationCircleIcon} status="danger" headingLevel="h2" variant="lg">
    <EmptyStateBody>{message}</EmptyStateBody>
    <EmptyStateFooter>
      <EmptyStateActions>
        <Button variant="primary" onClick={onRetry}>
          Réessayer
        </Button>
      </EmptyStateActions>
    </EmptyStateFooter>
  </EmptyState>
);

/** A refetch failed while earlier data stays on screen. */
export const StaleDataAlert: React.FunctionComponent<{ message: string }> = ({ message }) => (
  <Alert
    variant="danger"
    isInline
    title="L'actualisation a échoué — données de la lecture précédente"
    className="pf-v6-u-mb-md"
  >
    {message}
  </Alert>
);

/** Secondary « Actualiser » button; stays mounted and spins during a refetch. */
export const RefreshButton: React.FunctionComponent<{ loading: boolean; onRefresh: () => void }> = ({
  loading,
  onRefresh,
}) => (
  <Button
    variant="secondary"
    icon={<SyncAltIcon />}
    isLoading={loading}
    spinnerAriaValueText="Actualisation en cours"
    onClick={() => {
      if (!loading) {
        onRefresh();
      }
    }}
  >
    Actualiser
  </Button>
);

export const FetchedAt: React.FunctionComponent<{ fetchedAt: string | null }> = ({ fetchedAt }) =>
  fetchedAt === null ? null : (
    <span className="pf-v6-u-font-size-sm pf-v6-u-text-color-subtle">Lu le {formatDateTimeWithSeconds(fetchedAt)}</span>
  );

/** « Exporter (CSV) » — always the whole period the backend returned, whatever the on-screen filters. */
export const CsvButton: React.FunctionComponent<{
  label: string;
  filename: string;
  build: () => string;
  isDisabled?: boolean;
}> = ({ label, filename, build, isDisabled = false }) => (
  <Button
    variant="secondary"
    icon={<DownloadIcon />}
    isDisabled={isDisabled}
    onClick={() => downloadTextFile(filename, build(), 'text/csv;charset=utf-8')}
  >
    {label}
  </Button>
);

/** The response's warnings (and the conditions they describe) as French banners, most severe first. */
export const ReconciliationBanners: React.FunctionComponent<{
  warnings: ReconciliationWarning[];
  context?: BannerContext;
  onRetry: () => void;
}> = ({ warnings, context, onRetry }) => {
  const banners = buildBanners(warnings, context);
  if (banners.length === 0) {
    return null;
  }
  return (
    <div className="pf-v6-u-mb-md">
      {banners.map((b) => (
        <Alert
          key={b.code}
          variant={b.variant}
          isInline
          title={b.title}
          className="pf-v6-u-mb-sm"
          actionLinks={b.retry ? <AlertActionLink onClick={onRetry}>Actualiser</AlertActionLink> : undefined}
        >
          {b.body}
        </Alert>
      ))}
    </div>
  );
};

// ── Period pickers ───────────────────────────────────────────────────────────────────────────────────

/** ‹ month › : previous / next buttons around a month select (January 2000 … the current month). */
export const MonthPicker: React.FunctionComponent<{ month: string; onChange: (month: string) => void }> = ({
  month,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const options = monthOptions(month);
  const previous = shiftMonth(month, -1);
  const next = shiftMonth(month, 1);
  return (
    <Flex
      spaceItems={{ default: 'spaceItemsXs' }}
      alignItems={{ default: 'alignItemsCenter' }}
      flexWrap={{ default: 'nowrap' }}
    >
      <FlexItem>
        <Button
          variant="plain"
          aria-label="Mois précédent"
          icon={<AngleLeftIcon />}
          isDisabled={!isSelectableMonth(previous)}
          onClick={() => onChange(previous)}
        />
      </FlexItem>
      <FlexItem>
        <Select
          isOpen={isOpen}
          selected={month}
          onSelect={(_e, value) => {
            if (typeof value === 'string') {
              onChange(value);
            }
            setIsOpen(false);
          }}
          onOpenChange={setIsOpen}
          isScrollable
          toggle={(toggleRef) => (
            <MenuToggle
              ref={toggleRef}
              onClick={() => setIsOpen(!isOpen)}
              isExpanded={isOpen}
              aria-label="Choisir le mois"
              style={{ minWidth: '12rem' }}
            >
              {formatMonthYear(month)}
            </MenuToggle>
          )}
        >
          <SelectList>
            {options.map((m) => (
              <SelectOption key={m} value={m} isSelected={m === month}>
                {formatMonthYear(m)}
              </SelectOption>
            ))}
          </SelectList>
        </Select>
      </FlexItem>
      <FlexItem>
        <Button
          variant="plain"
          aria-label="Mois suivant"
          icon={<AngleRightIcon />}
          isDisabled={!isSelectableMonth(next)}
          onClick={() => onChange(next)}
        />
      </FlexItem>
    </Flex>
  );
};

/** ‹ year › : previous / next buttons around a year select (2000 … the current year). */
export const YearPicker: React.FunctionComponent<{ year: number; onChange: (year: number) => void }> = ({
  year,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const options = yearOptions();
  const latest = options[0];
  return (
    <Flex
      spaceItems={{ default: 'spaceItemsXs' }}
      alignItems={{ default: 'alignItemsCenter' }}
      flexWrap={{ default: 'nowrap' }}
    >
      <FlexItem>
        <Button
          variant="plain"
          aria-label="Année précédente"
          icon={<AngleLeftIcon />}
          isDisabled={year <= MIN_YEAR}
          onClick={() => onChange(year - 1)}
        />
      </FlexItem>
      <FlexItem>
        <Select
          isOpen={isOpen}
          selected={year}
          onSelect={(_e, value) => {
            if (typeof value === 'number') {
              onChange(value);
            }
            setIsOpen(false);
          }}
          onOpenChange={setIsOpen}
          isScrollable
          toggle={(toggleRef) => (
            <MenuToggle
              ref={toggleRef}
              onClick={() => setIsOpen(!isOpen)}
              isExpanded={isOpen}
              aria-label="Choisir l'année"
              style={{ minWidth: '7rem' }}
            >
              {year}
            </MenuToggle>
          )}
        >
          <SelectList>
            {options.map((y) => (
              <SelectOption key={y} value={y} isSelected={y === year}>
                {y}
              </SelectOption>
            ))}
          </SelectList>
        </Select>
      </FlexItem>
      <FlexItem>
        <Button
          variant="plain"
          aria-label="Année suivante"
          icon={<AngleRightIcon />}
          isDisabled={year >= latest}
          onClick={() => onChange(year + 1)}
        />
      </FlexItem>
    </Flex>
  );
};

// ── Client-side list state (search + filters + pagination) ───────────────────────────────────────────

/** True when `query` (already lowercased) is a substring of one of the fields. Empty query → true. */
export function matchesQuery(query: string, fields: (string | number | null | undefined | string[])[]): boolean {
  if (query === '') {
    return true;
  }
  return fields.some((field) => {
    if (field === null || field === undefined) {
      return false;
    }
    const values = Array.isArray(field) ? field : [String(field)];
    return values.some((v) => v.toLowerCase().includes(query));
  });
}

export const DEFAULT_PER_PAGE = 25;
export const PER_PAGE_OPTIONS = [
  { title: '10', value: 10 },
  { title: '25', value: 25 },
  { title: '50', value: 50 },
  { title: '100', value: 100 },
];

export interface ClientList<T> {
  searchValue: string;
  setSearchValue: (value: string) => void;
  /** Search and every filter of the list are at their defaults. */
  isFiltered: boolean;
  filtered: T[];
  paginated: T[];
  page: number;
  perPage: number;
  setPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
}

/**
 * Mode A (client-side) list state: a 400 ms debounced search, the caller's secondary-filter predicate
 * (`filterKey` changes whenever a filter does), and top/bottom pagination reset to page 1 on any change.
 */
export function useClientList<T>(
  rows: T[],
  matches: (row: T, query: string) => boolean,
  predicate: ((row: T) => boolean) | null = null,
  filterKey: string = '',
): ClientList<T> {
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearch = useDebouncedValue(searchValue, 400);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const query = debouncedSearch.trim().toLowerCase();

  useEffect(() => {
    setPage(1);
  }, [query, filterKey, rows]);

  const filtered = useMemo(
    () => rows.filter((row) => (predicate === null || predicate(row)) && matches(row, query)),
    // `matches` is a module-level function by convention; `filterKey` stands for `predicate`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, query, filterKey],
  );
  const paginated = useMemo(() => filtered.slice((page - 1) * perPage, page * perPage), [filtered, page, perPage]);

  return {
    searchValue,
    setSearchValue,
    isFiltered: query !== '' || filterKey !== '',
    filtered,
    paginated,
    page,
    perPage,
    setPage,
    setPerPage: (pp: number) => {
      setPerPage(pp);
      setPage(1);
    },
  };
}

export const ListPagination: React.FunctionComponent<{
  list: ClientList<unknown>;
  variant?: 'bottom';
  /** What one paginated item is (« lignes » by default; the accounting entries paginate « écritures »). */
  itemsLabel?: string;
}> = ({ list, variant, itemsLabel = 'lignes' }) => (
  <Pagination
    itemCount={list.filtered.length}
    perPage={list.perPage}
    page={list.page}
    perPageOptions={PER_PAGE_OPTIONS}
    onSetPage={(_e, p) => list.setPage(p)}
    onPerPageSelect={(_e, pp) => list.setPerPage(pp)}
    variant={variant}
    isCompact={variant !== 'bottom'}
    titles={{
      items: itemsLabel,
      page: 'page',
      itemsPerPage: 'Lignes par page',
      perPageSuffix: 'par page',
      toFirstPageAriaLabel: 'Première page',
      toPreviousPageAriaLabel: 'Page précédente',
      toNextPageAriaLabel: 'Page suivante',
      toLastPageAriaLabel: 'Dernière page',
      currPageAriaLabel: 'Page actuelle',
      paginationAriaLabel: 'Pagination',
      ofWord: 'sur',
    }}
  />
);

/** Search + the list's filters + optional actions (CSV) + the top pagination. */
export const ListToolbar: React.FunctionComponent<{
  list: ClientList<unknown>;
  placeholder: string;
  filters?: React.ReactNode;
  actions?: React.ReactNode;
  itemsLabel?: string;
}> = ({ list, placeholder, filters, actions, itemsLabel }) => (
  <Toolbar>
    <ToolbarContent>
      <ToolbarItem style={{ minWidth: '16rem' }}>
        <SearchInput
          placeholder={placeholder}
          value={list.searchValue}
          onChange={(_e, v) => list.setSearchValue(v)}
          onClear={() => list.setSearchValue('')}
        />
      </ToolbarItem>
      {filters}
      {actions && <ToolbarItem>{actions}</ToolbarItem>}
      <ToolbarItem variant="pagination">
        <ListPagination list={list} itemsLabel={itemsLabel} />
      </ToolbarItem>
    </ToolbarContent>
  </Toolbar>
);

/** « Aucune ligne ne correspond » — distinct from the empty-list state. */
export const NoMatchState: React.FunctionComponent<{ onClear: () => void }> = ({ onClear }) => (
  <EmptyState titleText="Aucune ligne ne correspond" icon={SearchIcon} headingLevel="h3" variant="sm">
    <EmptyStateBody>Modifiez la recherche ou les filtres, ou effacez-les pour tout afficher.</EmptyStateBody>
    <EmptyStateFooter>
      <EmptyStateActions>
        <Button variant="link" onClick={onClear}>
          Effacer les filtres
        </Button>
      </EmptyStateActions>
    </EmptyStateFooter>
  </EmptyState>
);

/** The list has no row at all for the period. */
export const EmptyListState: React.FunctionComponent<{ title: string; body?: string }> = ({ title, body }) => (
  <EmptyState titleText={title} headingLevel="h3" variant="sm">
    {body && <EmptyStateBody>{body}</EmptyStateBody>}
  </EmptyState>
);

/** A single-choice filter select (« Tous » + options). */
export const ChoiceFilter: React.FunctionComponent<{
  ariaLabel: string;
  allLabel: string;
  value: string | null;
  options: { value: string; label: string }[];
  onChange: (value: string | null) => void;
}> = ({ ariaLabel, allLabel, value, options, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const current = options.find((o) => o.value === value)?.label ?? allLabel;
  return (
    <Select
      isOpen={isOpen}
      selected={value ?? '__all__'}
      onSelect={(_e, v) => {
        onChange(typeof v === 'string' && v !== '__all__' ? v : null);
        setIsOpen(false);
      }}
      onOpenChange={setIsOpen}
      isScrollable
      toggle={(toggleRef) => (
        <MenuToggle ref={toggleRef} onClick={() => setIsOpen(!isOpen)} isExpanded={isOpen} aria-label={ariaLabel}>
          {current}
        </MenuToggle>
      )}
    >
      <SelectList>
        <SelectOption value="__all__" isSelected={value === null}>
          {allLabel}
        </SelectOption>
        {options.map((o) => (
          <SelectOption key={o.value} value={o.value} isSelected={o.value === value}>
            {o.label}
          </SelectOption>
        ))}
      </SelectList>
    </Select>
  );
};

/** The distinct values of `values` as `{value, label}` options, sorted by label (for filter selects). */
export function distinctOptions(
  values: (string | null)[],
  labelFor: (value: string) => string,
): { value: string; label: string }[] {
  const unique = Array.from(new Set(values.filter((v): v is string => v !== null && v !== '')));
  return unique
    .map((value) => ({ value, label: labelFor(value) }))
    .sort((a, b) => a.label.localeCompare(b.label, 'fr'));
}

// ── A small searchable table (the overview's lists) ──────────────────────────────────────────────────

export interface SimpleColumn<T> {
  title: string;
  cell: (row: T) => React.ReactNode;
  /** Right-aligned (amounts, counts). */
  numeric?: boolean;
}

/**
 * The full list pattern (search, top/bottom pagination, no-match state) for a short list with fixed
 * columns. Totals stay the backend's — pass them as `footer`, never re-summed from the visible rows.
 */
export function SearchableTable<T>({
  rows,
  rowKey,
  columns,
  matches,
  placeholder,
  ariaLabel,
  emptyTitle,
  footer,
}: {
  rows: T[];
  rowKey: (row: T, index: number) => string;
  columns: SimpleColumn<T>[];
  matches: (row: T, query: string) => boolean;
  placeholder: string;
  ariaLabel: string;
  emptyTitle: string;
  footer?: React.ReactNode;
}): React.ReactElement {
  const list = useClientList(rows, matches);
  if (rows.length === 0) {
    return <EmptyListState title={emptyTitle} />;
  }
  return (
    <>
      <ListToolbar list={list} placeholder={placeholder} />
      {list.filtered.length === 0 ? (
        <NoMatchState onClear={() => list.setSearchValue('')} />
      ) : (
        <>
          <Table aria-label={ariaLabel} variant="compact">
            <Thead>
              <Tr>
                {columns.map((c) => (
                  <Th key={c.title} className={c.numeric ? AMOUNT_CELL : undefined} modifier="wrap">
                    {c.title}
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {list.paginated.map((row, i) => (
                <Tr key={rowKey(row, i)}>
                  {columns.map((c) => (
                    <Td key={c.title} dataLabel={c.title} className={c.numeric ? AMOUNT_CELL : undefined}>
                      {c.cell(row)}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
          <ListPagination list={list} variant="bottom" />
        </>
      )}
      {footer}
    </>
  );
}
